import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bell, X, Phone, Mail, MessageSquare, Save } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const inputCls = "w-full bg-[#050505] border border-white/10 px-3 py-2 font-mono text-sm text-zinc-100 rounded-none focus:outline-none focus:ring-1 focus:ring-nc-blue focus:border-nc-blue";
const EVENTS = [
  ["new_lead", "New lead assigned to you"],
  ["deal_closed", "Deal closed / commission locked"],
  ["payout", "Commission payout"],
  ["support_reply", "Support ticket reply"],
];

export default function SalesNotifications({ me }) {
  const [open, setOpen] = useState(false);
  const [phone, setPhone] = useState(me?.phone || "");
  const [prefs, setPrefs] = useState(me?.notification_prefs || {});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      api.get("/auth/me").then((r) => {
        setPhone(r.data.phone || "");
        setPrefs(r.data.notification_prefs || {});
      }).catch(() => {});
    }
  }, [open]);

  const toggle = (event, channel) =>
    setPrefs((p) => ({ ...p, [event]: { email: true, sms: false, ...(p[event] || {}), [channel]: !(p[event]?.[channel]) } }));

  const save = async () => {
    setBusy(true);
    try {
      await api.patch("/profile", {
        name: me.name || "Sales Rep",
        bio: me.bio || "",
        phone: phone.trim(),
        notification_prefs: prefs,
      });
      toast.success("Notification preferences saved");
      setOpen(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button data-testid="sales-notifications-button" onClick={() => setOpen(true)} className="font-mono text-[10px] tracking-[0.2em] uppercase px-3 py-2 border border-white/15 text-zinc-300 hover:border-nc-orange/60 hover:text-nc-orange transition-colors flex items-center gap-2">
        <Bell className="w-3.5 h-3.5" /> Alerts
      </button>
      {open && (
        <div className="fixed inset-0 z-[95] bg-black/85 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div data-testid="sales-notifications-modal" className="border border-nc-orange/40 bg-[#0a0a0a] max-w-md w-full p-6 flex flex-col gap-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <p className="font-mono text-[10px] tracking-[0.3em] text-nc-orange uppercase flex items-center gap-2"><Bell className="w-4 h-4" /> Notification Alerts</p>
              <button data-testid="sales-notifications-close" onClick={() => setOpen(false)} className="text-zinc-500 hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <p className="font-mono text-[10px] text-zinc-500 leading-relaxed">
              Choose what reaches your email inbox and your phone via SMS. SMS requires your phone number in international format.
            </p>
            <div>
              <label className="font-mono text-[9px] tracking-[0.25em] text-zinc-500 uppercase block mb-1.5 flex items-center gap-1.5"><Phone className="w-3 h-3" /> Your phone (SMS alerts)</label>
              <input data-testid="sales-notifications-phone" className={inputCls} placeholder="+61412345678" value={phone} onChange={(e) => setPhone(e.target.value)} maxLength={20} />
            </div>
            <div className="border border-white/10">
              <div className="grid grid-cols-[1fr_64px_64px] border-b border-white/10 px-3 py-2">
                <span className="font-mono text-[9px] tracking-[0.2em] text-zinc-500 uppercase">Event</span>
                <span className="font-mono text-[9px] tracking-[0.2em] text-zinc-500 uppercase text-center flex items-center justify-center gap-1"><Mail className="w-3 h-3" /> Email</span>
                <span className="font-mono text-[9px] tracking-[0.2em] text-zinc-500 uppercase text-center flex items-center justify-center gap-1"><MessageSquare className="w-3 h-3" /> SMS</span>
              </div>
              {EVENTS.map(([key, label]) => (
                <div key={key} data-testid={`sales-notif-row-${key}`} className="grid grid-cols-[1fr_64px_64px] items-center border-b border-white/5 px-3 py-2.5">
                  <span className="font-mono text-[10px] text-zinc-300">{label}</span>
                  <span className="flex justify-center">
                    <input type="checkbox" data-testid={`sales-notif-${key}-email`} checked={prefs[key]?.email ?? true} onChange={() => toggle(key, "email")} className="w-4 h-4 accent-[#2E6BFF]" />
                  </span>
                  <span className="flex justify-center">
                    <input type="checkbox" data-testid={`sales-notif-${key}-sms`} checked={prefs[key]?.sms ?? false} onChange={() => toggle(key, "sms")} className="w-4 h-4 accent-[#C74A00]" />
                  </span>
                </div>
              ))}
            </div>
            <button data-testid="sales-notifications-save" disabled={busy} onClick={save} className="bg-nc-orange hover:bg-nc-orange/80 disabled:opacity-40 text-black font-sans font-semibold uppercase tracking-[0.2em] text-xs px-4 py-3 flex items-center justify-center gap-2 transition-colors">
              <Save className="w-4 h-4" /> Save Preferences
            </button>
          </div>
        </div>
      )}
    </>
  );
}
